import { type FC, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { Button } from '../../../components/Button/Button'
import { LinkButton } from '../../../components/Button/LinkButton'
import { IntegrationIcon } from '../../../components/IntegrationIcon'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import { useGetMeQuery } from '../../../store/user/user'
import { useGetUserSchedulingSettingsQuery, useUpdateUserSchedulingSettingsMutation } from '../../../store/scheduling/scheduling'
import { CalendarProvider, MeetingPlatformProvider } from '../../../models/Scheduling'
import { OnboardingStepHeader } from '../../Growth/Onboarding/OnboardingStepHeader/OnboardingStepHeader'
import { useIntegrations } from '../../Integration/useIntegrations'
import { ConnectedDetail, IntegrationOptionRow, ReconnectDetail, SyncOffDetail } from './IntegrationOptionRow'
import {
  calendarSyncSettingsPath,
  type CalendarSyncCopy,
  integrationConnectPath,
  NATIVE_PLATFORM_BY_CALENDAR,
  needsOutlookAdminApproval,
  SCHEDULING_INTEGRATIONS_PATH,
} from '../../ProfileSettings/Scheduling/sections/schedulingIntegrationStatus'
import { OutlookApprovalNotice } from '../shared/OutlookApprovalNotice'
import { isCapable } from '../flows/form/scheduleHelpers'
import { OnboardingStepContent } from './OnboardingStepFrame'
import { type CalendarOnboardingOption, getIntegrationEmail, IntegrationConnectionStatus, resolveCalendarOptions } from './onboardingIntegrations'

const RECONNECT_TOOLTIP = "Scheduling can't add meetings to this calendar yet. Reconnecting doesn't affect your meeting notes."
const SYNC_OFF_CTA_LABEL = 'Turn on calendar sync'

const singleCalendarCopy = (option: CalendarOnboardingOption): { title: string; subtitle: string } => {
  if (option.status === IntegrationConnectionStatus.READY) {
    return { title: 'Confirm your scheduling calendar', subtitle: 'Scheduling adds booked meetings to this calendar.' }
  }
  if (option.status === IntegrationConnectionStatus.NEEDS_CALENDAR_SYNC) {
    const { title, subtitle } = option.calendarSync as CalendarSyncCopy
    return { title, subtitle }
  }
  return {
    title: 'Allow Scheduling to add meetings',
    subtitle: "Your calendar is connected but Scheduling can't add meetings to it yet. Reconnect to start scheduling.",
  }
}

const getCalendarCardColumns = (optionCount: number): number => {
  if (optionCount <= 3) {
    return optionCount
  }
  if (optionCount % 3 === 0) {
    return 3
  }
  return optionCount % 2 === 0 ? 2 : 3
}

const chunkIntoRows = <T,>(items: T[], columns: number): T[][] =>
  items.reduce<T[][]>((rows, item, index) => {
    const rowIndex = Math.floor(index / columns)
    const nextRows = [...rows]
    nextRows[rowIndex] = [...(nextRows[rowIndex] ?? []), item]
    return nextRows
  }, [])

export interface CalendarStepProps {
  onCompleted?: () => void
}

export const CalendarStep: FC<CalendarStepProps> = ({ onCompleted }) => {
  const dispatch = useAppDispatch()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const { accountId, id: userId } = authenticatedUser
  const { data: me } = useGetMeQuery({ accountId })
  const { data: userSettings } = useGetUserSchedulingSettingsQuery({ accountId, userId })
  const { isIntegrationAllowed, isLoading: isIntegrationsLoading } = useIntegrations()
  const [updateUserSettings, { isLoading: isSelecting }] = useUpdateUserSchedulingSettingsMutation()
  const [selectedProvider, setSelectedProvider] = useState<CalendarProvider>()

  if (!userSettings || isIntegrationsLoading) {
    return null
  }

  const outlookNeedsApproval = needsOutlookAdminApproval(userSettings.integrations)
  const options = resolveCalendarOptions(userSettings.integrations, isIntegrationAllowed)
  const connected = options.filter((option) => option.status !== IntegrationConnectionStatus.NOT_CONNECTED)
  const connectable = options.filter((option) => option.status === IntegrationConnectionStatus.NOT_CONNECTED)

  const selectCalendar = async (provider: CalendarProvider) => {
    const currentPlatform = userSettings.integrations.selectedMeetingPlatform
    const isCurrentPlatformNative = currentPlatform === MeetingPlatformProvider.GOOGLE_MEET || currentPlatform === MeetingPlatformProvider.MS_TEAMS
    const isNativeMismatch = isCurrentPlatformNative && currentPlatform !== NATIVE_PLATFORM_BY_CALENDAR[provider]

    const zoomIntegration = userSettings.integrations.meetingPlatforms.find((platform) => platform.provider === MeetingPlatformProvider.ZOOM)
    const isStaleZoomSelection = currentPlatform === MeetingPlatformProvider.ZOOM && !(zoomIntegration != null && isCapable(zoomIntegration))

    const clearsMeetingPlatform = isNativeMismatch || isStaleZoomSelection

    const result = await updateUserSettings({
      accountId,
      userId,
      update: {
        integrations: {
          ...userSettings.integrations,
          selectedCalendar: provider,
          selectedMeetingPlatform: clearsMeetingPlatform ? null : currentPlatform,
        },
      },
    })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not select the calendar', messages: ['Please try again.'] }))
    } else {
      onCompleted?.()
    }
  }

  const manageLink =
    connectable.length > 0 ? (
      <LinkButton variant='primaryLinkButton' to={SCHEDULING_INTEGRATIONS_PATH} className='text-decoration-none'>
        Manage your calendar integrations
      </LinkButton>
    ) : null

  if (connected.length === 0) {
    return (
      <>
        <OnboardingStepHeader
          icon={<i className='bi bi-calendar2' />}
          title='Connect your calendar'
          subtitle='Scheduling books meetings straight into your calendar. Connect one to get started.'
        />
        <OnboardingStepContent>
          <div className='d-flex flex-column gap-3 w-100'>
            {chunkIntoRows(connectable, getCalendarCardColumns(connectable.length)).map((row) => (
              <Row key={row.map((option) => option.serviceId).join('-')} className='justify-content-center g-3'>
                {row.map((option) => (
                  <Col key={option.serviceId} xs={12 / getCalendarCardColumns(connectable.length)}>
                    <div className='d-flex flex-column align-items-center text-center gap-2 p-4 border rounded-3 zocks-border-color-gray-300 h-100'>
                      <IntegrationIcon serviceId={option.serviceId} width='1.5rem' height='1.5rem' />
                      <span className='paragraph-small fw-bold zocks-text-gray-900'>{option.label}</span>
                      <LinkButton to={integrationConnectPath(option.serviceId)} variant='primaryLinkButton' className='text-decoration-none mt-auto'>
                        Connect
                      </LinkButton>
                    </div>
                  </Col>
                ))}
              </Row>
            ))}
          </div>
        </OnboardingStepContent>
      </>
    )
  }

  if (connected.length === 1) {
    const option = connected[0]
    const email = getIntegrationEmail(me?.integrations, option.serviceId)
    const isReady = option.status === IntegrationConnectionStatus.READY
    const needsCalendarSync = option.status === IntegrationConnectionStatus.NEEDS_CALENDAR_SYNC
    const { title, subtitle } = singleCalendarCopy(option)

    return (
      <>
        <OnboardingStepHeader icon={<i className='bi bi-calendar2' />} title={title} subtitle={subtitle} />
        <OnboardingStepContent>
          <div className='d-flex flex-column gap-3 p-3 border rounded-3 zocks-border-color-gray-300 w-100'>
            <div className='d-flex align-items-center gap-2'>
              <IntegrationIcon serviceId={option.serviceId} width='1.5rem' height='1.5rem' />
              <span className='d-flex flex-column overflow-hidden'>
                <span className='paragraph-small fw-bold zocks-text-gray-900'>{option.label}</span>
                {isReady ? <ConnectedDetail email={email} /> : <span className='paragraph-small zocks-text-gray-600 text-truncate'>{email}</span>}
              </span>
            </div>
            {option.provider === CalendarProvider.OUTLOOK && outlookNeedsApproval ? <OutlookApprovalNotice /> : null}
            {isReady ? (
              <Button variant='primary' className='align-self-stretch' loading={isSelecting} onClick={() => void selectCalendar(option.provider)}>
                Select calendar
              </Button>
            ) : needsCalendarSync ? (
              <LinkButton to={calendarSyncSettingsPath(option.serviceId)} variant='primary' className='align-self-stretch'>
                {SYNC_OFF_CTA_LABEL}
              </LinkButton>
            ) : (
              <LinkButton to={integrationConnectPath(option.serviceId)} variant='primary' className='align-self-stretch'>
                Reconnect
              </LinkButton>
            )}
          </div>
          {manageLink}
        </OnboardingStepContent>
      </>
    )
  }

  const selectedOption =
    connected.find((option) => option.provider === selectedProvider) ??
    connected.find((option) => option.provider === userSettings.integrations.selectedCalendar) ??
    connected[0]
  const isSelectedReady = selectedOption.status === IntegrationConnectionStatus.READY
  const isSelectedSyncOff = selectedOption.status === IntegrationConnectionStatus.NEEDS_CALENDAR_SYNC

  return (
    <>
      <OnboardingStepHeader
        icon={<i className='bi bi-calendar2' />}
        title='Choose your scheduling calendar'
        subtitle='Scheduling adds booked meetings to one calendar. Choose which one to use.'
      />
      <OnboardingStepContent>
        <div role='radiogroup' aria-label='Scheduling calendar' className='d-flex flex-column gap-2 w-100'>
          {connected.map((option) => (
            <IntegrationOptionRow
              key={option.serviceId}
              serviceId={option.serviceId}
              label={option.label}
              detail={
                option.status === IntegrationConnectionStatus.READY ? (
                  <ConnectedDetail email={getIntegrationEmail(me?.integrations, option.serviceId)} />
                ) : option.status === IntegrationConnectionStatus.NEEDS_CALENDAR_SYNC ? (
                  <SyncOffDetail tooltip={(option.calendarSync as CalendarSyncCopy).tooltip} />
                ) : (
                  <ReconnectDetail email={getIntegrationEmail(me?.integrations, option.serviceId)} tooltip={RECONNECT_TOOLTIP} />
                )
              }
              selected={option.provider === selectedOption.provider}
              onSelect={() => setSelectedProvider(option.provider)}
              notice={option.provider === CalendarProvider.OUTLOOK && outlookNeedsApproval ? <OutlookApprovalNotice /> : undefined}
            />
          ))}
        </div>
        {isSelectedReady ? (
          <Button variant='primary' className='align-self-stretch' loading={isSelecting} onClick={() => void selectCalendar(selectedOption.provider)}>
            Select calendar
          </Button>
        ) : isSelectedSyncOff ? (
          <LinkButton to={calendarSyncSettingsPath(selectedOption.serviceId)} variant='primary' className='align-self-stretch'>
            {SYNC_OFF_CTA_LABEL}
          </LinkButton>
        ) : (
          <LinkButton to={integrationConnectPath(selectedOption.serviceId)} variant='primary' className='align-self-stretch'>
            Reconnect
          </LinkButton>
        )}
      </OnboardingStepContent>
    </>
  )
}
